/**
 * bomberDroneDrop.ts
 * Pool + lifecycle helpers for `ClusterBomb` and `BombShrapnel`.
 * Sibling of `fighterDroneFire.ts` — same module-singleton pool +
 * acquire-on-spawn + per-tick update + bulk-release shape, but owns
 * TWO pools (the parent payload and its post-fuse fragments) because
 * detonation always crosses from one to the other.
 *
 * Owns:
 *  - The `ClusterBomb` pool (capped at `BOMBER_DRONE_BOMB_POOL_CAP`,
 *    pre-warmed at module load),
 *  - The `BombShrapnel` pool (capped at
 *    `BOMBER_DRONE_SHRAPNEL_POOL_CAP`, pre-warmed at module load),
 *  - `dropClusterBomb(x, y)` — single drop, returns `false` on pool
 *    miss so the caller can leave its cadence timer parked and retry,
 *  - `updateBomberOrdnance(dt)` — per-tick integrate + cull for both
 *    pools, plus the bomb-detonation → shrapnel-spawn handoff,
 *  - `releaseAllBomberOrdnance()` — bulk release on scene teardown.
 *
 * Kept separate from `spaceEnemySystem` so `BomberDrone.update()` can
 * import the drop helper without dragging the enemy spawner / cull
 * pipeline into the entity layer. The same rationale as
 * `fighterDroneFire`.
 *
 * DECISION: Both pools live in one module because the lifecycle is
 * coupled — a bomb's detonation IS a shrapnel spawn — and splitting
 * them would leave one module reaching into the other on every
 * detonation tick. Bundling them keeps the cross-pool acquisition
 * inside the file boundary that already understands both pools.
 *
 * NO React imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module bomberDroneDrop
 */

import { spaceState } from './spaceState';
import type { LevelRuntime } from './gameState';
import { ClusterBomb } from '../entities/ClusterBomb';
import { BombShrapnel } from '../entities/BombShrapnel';
import { ObjectPool } from '../utils/objectPool';
import {
  BOMBER_DRONE_BOMB_POOL_CAP,
  BOMBER_DRONE_SHRAPNEL_POOL_CAP,
  BOMBER_DRONE_SHRAPNEL_COUNT,
  BOMBER_DRONE_SHRAPNEL_WIDTH,
  BOMBER_DRONE_SHRAPNEL_HEIGHT,
} from '../../constants/spaceEnemyConstants';

/** Reusable empty level for the ordnance update — space has no platforms. */
const EMPTY_SPACE_LEVEL: LevelRuntime = { width: 0, height: 0, platforms: [] };

/**
 * Cluster-bomb pool. Pre-warmed at module load so the first volley
 * doesn't pay an allocation cost. Sized to
 * `BOMBER_DRONE_BOMB_POOL_CAP` — covers an unlucky tick where every
 * bomber on screen drops simultaneously.
 */
const clusterBombPool = new ObjectPool<ClusterBomb>(
  () => new ClusterBomb(),
  BOMBER_DRONE_BOMB_POOL_CAP,
);
clusterBombPool.prewarm(BOMBER_DRONE_BOMB_POOL_CAP);

/**
 * Shrapnel pool. Sized to
 * `BOMBER_DRONE_SHRAPNEL_POOL_CAP` (= 48), giving headroom for ~8
 * simultaneous detonations of 6 pieces each. Realistic concurrent
 * live count is far lower (TTL is short, drops are spaced) — this is
 * the worst-case envelope and still sits below the AGENTS.md 50-cap
 * on pooled hazard objects.
 */
const bombShrapnelPool = new ObjectPool<BombShrapnel>(
  () => new BombShrapnel(),
  BOMBER_DRONE_SHRAPNEL_POOL_CAP,
);
bombShrapnelPool.prewarm(BOMBER_DRONE_SHRAPNEL_POOL_CAP);

/**
 * Test-only helper: number of bomb instances currently parked in the
 * pool (i.e. NOT live in `spaceState.clusterBombs`).
 *
 * @returns Count of available pooled instances.
 * @internal
 */
export function _clusterBombPoolAvailable(): number {
  return clusterBombPool.available;
}

/**
 * Test-only helper: number of shrapnel instances currently parked in
 * the pool (i.e. NOT live in `spaceState.bombShrapnel`).
 *
 * @returns Count of available pooled instances.
 * @internal
 */
export function _bombShrapnelPoolAvailable(): number {
  return bombShrapnelPool.available;
}

/**
 * Drop a single cluster bomb at the given world-space coordinate.
 * Returns `true` when a bomb was spawned, `false` when the pool was
 * exhausted. Callers (`BomberDrone.update()`) use the return value to
 * leave their drop-cadence timer parked on a miss so a dropped spawn
 * doesn't burn a full cadence interval.
 *
 * @param x - World-space X of the bomb's spawn (top-left of hitbox).
 * @param y - World-space Y of the bomb's spawn (top-left of hitbox).
 * @returns `true` when a bomb was spawned, `false` on a pool miss.
 */
export function dropClusterBomb(x: number, y: number): boolean {
  const bomb = clusterBombPool.acquire();
  if (bomb === null) return false;
  bomb.spawn(x, y);
  spaceState.clusterBombs.push(bomb);
  return true;
}

/**
 * Spawn `BOMBER_DRONE_SHRAPNEL_COUNT` shrapnel pieces in an evenly
 * spaced radial fan centred on the given world-space coordinate.
 * Headings are `i * 2π / COUNT` with a 1/COUNT-turn phase offset so
 * no piece flies straight along an axis (avoids the visual of a
 * shrapnel piece travelling on the same vector as the ship's auto-fire
 * lane). Silently spawns as many as the pool can satisfy — a pool
 * miss in the middle of a fan just means the player gets fewer
 * fragments to dodge.
 *
 * DECISION: Inlined here rather than as a `ClusterBomb` method
 * because the entity has no business knowing about the shrapnel pool.
 * Cross-pool acquisition belongs in the lifecycle owner — see the
 * module-level rationale above.
 *
 * @param centreX - World-space X of the detonation centre.
 * @param centreY - World-space Y of the detonation centre.
 */
function spawnShrapnelFan(centreX: number, centreY: number): void {
  const count = BOMBER_DRONE_SHRAPNEL_COUNT;
  // Half-step phase offset so no piece flies on a cardinal axis.
  const phaseOffset = Math.PI / count;
  // DECISION: `BombShrapnel.spawn` plants (x, y) as the hitbox top-left
  // (BaseEntity contract), but the bomb centre is the geometric origin
  // of the fan. Offset each spawn by half the shrapnel size so every
  // piece's centre coincides with the detonation centre — otherwise
  // the entire fan shifts down-right by (W/2, H/2).
  const halfW = BOMBER_DRONE_SHRAPNEL_WIDTH / 2;
  const halfH = BOMBER_DRONE_SHRAPNEL_HEIGHT / 2;
  for (let i = 0; i < count; i++) {
    const piece = bombShrapnelPool.acquire();
    if (piece === null) return; // Pool miss — stop the fan early.
    const heading = (i * 2 * Math.PI) / count + phaseOffset;
    piece.spawn(centreX - halfW, centreY - halfH, heading);
    spaceState.bombShrapnel.push(piece);
  }
}

/**
 * Update every active cluster bomb and shrapnel piece, run the
 * detonation → shrapnel handoff, and release dead instances back to
 * their pools.
 *
 * Order per tick: tick bombs (which may flip `detonated`) → reap
 * detonated bombs (spawning shrapnel for each) → reap dead-but-not-
 * detonated bombs (left-cull) → tick shrapnel → reap dead shrapnel.
 * Splitting the bomb reap into two phases keeps the shrapnel-spawn
 * call out of the integration loop and ensures any shrapnel spawned
 * this tick gets its own integration step before the renderer reads.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateBomberOrdnance(dt: number): void {
  const bombs = spaceState.clusterBombs;
  for (let i = bombs.length - 1; i >= 0; i--) {
    const bomb = bombs[i];
    bomb.update(dt, EMPTY_SPACE_LEVEL);
    if (bomb.dead) {
      // DECISION: Read `detonated` BEFORE release — release calls
      // `reset()` which clears the flag. Spawning shrapnel from a
      // recycled bomb's reset state would never fire.
      if (bomb.detonated) {
        spawnShrapnelFan(bomb.detonationCentreX(), bomb.detonationCentreY());
      }
      clusterBombPool.release(bomb);
      bombs.splice(i, 1);
    }
  }
  const shrapnel = spaceState.bombShrapnel;
  for (let i = shrapnel.length - 1; i >= 0; i--) {
    const piece = shrapnel[i];
    piece.update(dt, EMPTY_SPACE_LEVEL);
    if (piece.dead) {
      bombShrapnelPool.release(piece);
      shrapnel.splice(i, 1);
    }
  }
}

/**
 * Release every live cluster bomb and shrapnel piece back to their
 * pools and clear both scene-bookkeeping lists. Called from
 * `SpaceScene.init()` and `SpaceScene.destroy()` so a re-init of the
 * space level starts from a clean slate.
 */
export function releaseAllBomberOrdnance(): void {
  for (let i = 0; i < spaceState.clusterBombs.length; i++) {
    clusterBombPool.release(spaceState.clusterBombs[i]);
  }
  spaceState.clusterBombs.length = 0;
  for (let i = 0; i < spaceState.bombShrapnel.length; i++) {
    bombShrapnelPool.release(spaceState.bombShrapnel[i]);
  }
  spaceState.bombShrapnel.length = 0;
}
